import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  MagnifyingGlassIcon,
  FlagIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface Comment {
  id: string;
  author: {
    name: string;
    email: string;
    avatar?: string;
  };
  post: {
    title: string;
    creatorName: string;
  };
  content: string;
  createdAt: Date;
  reportCount: number;
  status: 'active' | 'flagged' | 'hidden' | 'deleted';
}

const AdminCommentsPage: React.FC = () => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    setLoading(true);
    try {
      // Mock data
      const mockData: Comment[] = [
        {
          id: '1',
          author: {
            name: 'John Doe',
            email: 'john@example.com',
            avatar: 'https://i.pravatar.cc/150?img=1',
          },
          post: { title: 'New artwork reveal', creatorName: 'Art Studio' },
          content: 'This is amazing! Love your work!',
          createdAt: new Date(Date.now() - 3600000),
          reportCount: 0,
          status: 'active',
        },
        {
          id: '2',
          author: {
            name: 'Jane Smith',
            email: 'jane@example.com',
            avatar: 'https://i.pravatar.cc/150?img=2',
          },
          post: { title: 'Behind the scenes', creatorName: 'Music Producer' },
          content: 'This content contains inappropriate language and spam links.',
          createdAt: new Date(Date.now() - 7200000),
          reportCount: 5,
          status: 'flagged',
        },
        {
          id: '3',
          author: {
            name: 'Mike Johnson',
            email: 'mike@example.com',
            avatar: 'https://i.pravatar.cc/150?img=3',
          },
          post: { title: 'Tutorial series', creatorName: 'Game Dev' },
          content: 'When will the next episode come out?',
          createdAt: new Date(Date.now() - 10800000),
          reportCount: 0,
          status: 'active',
        },
      ];
      setComments(mockData);
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'flagged':
        return 'bg-red-100 text-red-800';
      case 'hidden':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleApprove = async (id: string) => {
    console.log('Approving comment:', id);
    // Implement actual approval logic
  };

  const handleHide = async (id: string) => {
    console.log('Hiding comment:', id);
    // Implement actual hide logic
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    console.log('Deleting comment:', id);
    // Implement actual deletion logic
  };

  const filteredComments = comments.filter((comment) => {
    const matchesSearch =
      comment.author.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.post.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || comment.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: comments.length,
    active: comments.filter((c) => c.status === 'active').length,
    flagged: comments.filter((c) => c.status === 'flagged').length,
    hidden: comments.filter((c) => c.status === 'hidden').length,
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">Loading comments...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Comment Moderation</h1>
        <p className="mt-2 text-gray-600">Review and moderate user comments</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Comments</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Active</p>
            <p className="text-3xl font-bold text-green-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Flagged</p>
            <p className="text-3xl font-bold text-red-600">{stats.flagged}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Hidden</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.hidden}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search comments by author, content, or post..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="flagged">Flagged</option>
              <option value="hidden">Hidden</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Comments List */}
      <div className="space-y-4">
        {filteredComments.map((comment) => (
          <Card key={comment.id}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <img
                  src={comment.author.avatar || 'https://i.pravatar.cc/150'}
                  alt={comment.author.name}
                  className="w-12 h-12 rounded-full"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{comment.author.name}</p>
                      <p className="text-xs text-gray-500">
                        on "{comment.post.title}" by {comment.post.creatorName}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {comment.reportCount > 0 && (
                        <div className="flex items-center text-red-600">
                          <FlagIcon className="w-4 h-4 mr-1" />
                          <span className="text-sm font-medium">{comment.reportCount}</span>
                        </div>
                      )}
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(
                          comment.status
                        )}`}
                      >
                        {comment.status}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{comment.content}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      {new Date(comment.createdAt).toLocaleString()}
                    </p>
                    <div className="flex gap-2">
                      {comment.status === 'flagged' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleApprove(comment.id)}
                        >
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      )}
                      {comment.status !== 'hidden' && (
                        <Button variant="outline" size="sm" onClick={() => handleHide(comment.id)}>
                          Hide
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(comment.id)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminCommentsPage;
